/**
 * SaveManager - Handles save/load operations with named saves
 */

export interface WorldSaveData {
  worldId: string;
  worldType: 'overworld' | 'hell';
  seed: number;
  modifiedTiles: { x: number; y: number; type: string; resourceAmount: number; regrowthStage?: number; regrowthTimer?: number; wasTree?: boolean; wasBush?: boolean }[];
  playerLastPosition: { x: number; y: number; z: number };
  structures: {
    id: string;
    blueprintId: string;
    position: { x: number; y: number; z: number };
    health: number;
    campfire?: {
      fuel: number;
      isBurning: boolean;
      cookingItem: string | null;
      rawQuantity: number;
      cookingProgress: number;
      cookedQuantity: number;
    };
    storage?: {
      slots: ({ itemId: string; quantity: number } | null)[];
    };
  }[];
}

export interface SaveData {
  version: number;
  timestamp: number;
  name: string;
  player: {
    position: { x: number; y: number; z: number };
    health: number;
    maxHealth: number;
    stamina: number;
    maxStamina: number;
    level?: number;
    xp?: number;
    abilities?: string[];
  };
  inventory: {
    slots: ({ itemId: string; quantity: number } | null)[];
    selectedSlot: number;
  };
  equipment?: {
    slot: string;
    itemId: string;
  }[];
  time: {
    gameTime: number;
    day: number;
    totalPlayTime: number;
  };
  // Legacy single-world support (for backwards compatibility)
  world: {
    seed: number;
    modifiedTiles: { x: number; y: number; type: string; resourceAmount: number; regrowthStage?: number; regrowthTimer?: number; wasTree?: boolean; wasBush?: boolean }[];
  };
  structures: {
    id: string;
    blueprintId: string;
    position: { x: number; y: number; z: number };
    health: number;
    // Campfire data (for furnace structures)
    campfire?: {
      fuel: number;
      isBurning: boolean;
      cookingItem: string | null;
      rawQuantity: number;
      cookingProgress: number;
      cookedQuantity: number;
    };
    // Storage inventory (for storage structures)
    storage?: {
      slots: ({ itemId: string; quantity: number } | null)[];
    };
  }[];
  // Multi-world support (new)
  currentWorldId?: string;
  worlds?: WorldSaveData[];
}

export interface SaveSlotInfo {
  slot: number;
  exists: boolean;
  name: string;
  timestamp: number;
  playTime: number;
  day: number;
}

const SAVE_VERSION = 1;
const MAX_SAVE_SLOTS = 10;
const LOCAL_STORAGE_PREFIX = 'isometric_survival_';
const LAST_PLAYED_KEY = `${LOCAL_STORAGE_PREFIX}last_played`;

// Special slot number for the default demo save
export const DEFAULT_SAVE_SLOT = -1;

// Built-in end-game demo save - centered at (0,0) to fit within world bounds (-50 to 49)
const DEFAULT_SAVE_DATA: SaveData = {
  version: 1,
  timestamp: Date.now(),
  name: "End Game Demo",
  player: {
    position: { x: 0, y: 0, z: 0 },
    health: 100,
    maxHealth: 100,
    stamina: 100,
    maxStamina: 100,
    level: 40,
    xp: 200000,
    abilities: ["fly", "double_damage", "night_vision", "fast_build", "primitive_guns", "steady_aim", "modern_guns", "quick_reload", "futuristic_guns", "energy_efficiency", "master_gunner"]
  },
  inventory: {
    slots: [
      { itemId: "railgun", quantity: 1 },
      { itemId: "plasma_cannon", quantity: 1 },
      { itemId: "assault_rifle", quantity: 1 },
      { itemId: "sniper_rifle", quantity: 1 },
      { itemId: "shotgun_pump", quantity: 1 },
      { itemId: "laser_rifle", quantity: 1 },
      { itemId: "smg", quantity: 1 },
      { itemId: "pistol_9mm", quantity: 1 },
      { itemId: "rail_charge", quantity: 99 },
      { itemId: "rail_charge", quantity: 99 },
      { itemId: "plasma_cell", quantity: 99 },
      { itemId: "plasma_cell", quantity: 99 },
      { itemId: "energy_cell", quantity: 99 },
      { itemId: "energy_cell", quantity: 99 },
      { itemId: "rifle_ammo", quantity: 99 },
      { itemId: "rifle_ammo", quantity: 99 },
      { itemId: "pistol_ammo", quantity: 99 },
      { itemId: "pistol_ammo", quantity: 99 },
      { itemId: "shotgun_shells", quantity: 99 },
      { itemId: "shotgun_shells", quantity: 99 }
    ],
    selectedSlot: 0
  },
  equipment: [
    { slot: "head", itemId: "spectral_hood" },
    { slot: "chest", itemId: "spectral_robe" },
    { slot: "legs", itemId: "spectral_leggings" },
    { slot: "feet", itemId: "spectral_boots" },
    { slot: "hands", itemId: "spectral_gloves" },
    { slot: "accessory", itemId: "spectral_amulet" }
  ],
  time: {
    gameTime: 150,
    day: 50,
    totalPlayTime: 36000
  },
  world: {
    seed: 12345,
    modifiedTiles: []
  },
  structures: (() => {
    const structures: typeof DEFAULT_SAVE_DATA.structures = [];
    let floorId = 0;

    // Helper to add floor tiles
    const addFloor = (x: number, y: number) => {
      structures.push({ id: `floor_${floorId++}`, blueprintId: "floor_wood", position: { x, y, z: 0 }, health: 50 });
    };

    // ===== CENTRAL PLATFORM (17x17) - from -8 to 8 =====
    for (let y = -8; y <= 8; y++) {
      for (let x = -8; x <= 8; x++) {
        addFloor(x, y);
      }
    }

    // ===== NORTH PETAL (bridge going up from y=-9 to y=-16) =====
    // Bridge part (5 wide)
    for (let y = -9; y >= -16; y--) {
      addFloor(-2, y);
      addFloor(-1, y);
      addFloor(0, y);
      addFloor(1, y);
      addFloor(2, y);
    }
    // Petal end (7x4)
    for (let x = -3; x <= 3; x++) {
      addFloor(x, -17);
      addFloor(x, -18);
      addFloor(x, -19);
      addFloor(x, -20);
    }

    // ===== SOUTH PETAL (bridge going down from y=9 to y=16) =====
    for (let y = 9; y <= 16; y++) {
      addFloor(-2, y);
      addFloor(-1, y);
      addFloor(0, y);
      addFloor(1, y);
      addFloor(2, y);
    }
    // Petal end (7x4)
    for (let x = -3; x <= 3; x++) {
      addFloor(x, 17);
      addFloor(x, 18);
      addFloor(x, 19);
      addFloor(x, 20);
    }

    // ===== EAST PETAL (bridge going right from x=9 to x=16) =====
    for (let x = 9; x <= 16; x++) {
      addFloor(x, -2);
      addFloor(x, -1);
      addFloor(x, 0);
      addFloor(x, 1);
      addFloor(x, 2);
    }
    // Petal end (4x7)
    for (let y = -3; y <= 3; y++) {
      addFloor(17, y);
      addFloor(18, y);
      addFloor(19, y);
      addFloor(20, y);
    }

    // ===== WEST PETAL (bridge going left from x=-9 to x=-16) =====
    for (let x = -9; x >= -16; x--) {
      addFloor(x, -2);
      addFloor(x, -1);
      addFloor(x, 0);
      addFloor(x, 1);
      addFloor(x, 2);
    }
    // Petal end (4x7)
    for (let y = -3; y <= 3; y++) {
      addFloor(-17, y);
      addFloor(-18, y);
      addFloor(-19, y);
      addFloor(-20, y);
    }

    // ===== HELL HOLE IN THE CENTER =====
    structures.push({ id: "hell_hole_center", blueprintId: "hell_hole", position: { x: 0, y: 0, z: 0 }, health: 9999 });

    // ===== CENTRAL STRUCTURES =====
    // Workbenches around center (4 in a square, offset from hell hole)
    structures.push({ id: "struct_workbench_1", blueprintId: "workbench", position: { x: -4, y: -4, z: 0 }, health: 100 });
    structures.push({ id: "struct_workbench_2", blueprintId: "workbench", position: { x: 4, y: -4, z: 0 }, health: 100 });
    structures.push({ id: "struct_workbench_3", blueprintId: "workbench", position: { x: -4, y: 4, z: 0 }, health: 100 });
    structures.push({ id: "struct_workbench_4", blueprintId: "workbench", position: { x: 4, y: 4, z: 0 }, health: 100 });

    // Campfires at corners of center platform
    const campfireData = { fuel: 100, isBurning: true, cookingItem: null, rawQuantity: 0, cookingProgress: 0, cookedQuantity: 0 };
    structures.push({ id: "struct_campfire_1", blueprintId: "campfire", position: { x: -8, y: -8, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "struct_campfire_2", blueprintId: "campfire", position: { x: 8, y: -8, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "struct_campfire_3", blueprintId: "campfire", position: { x: -8, y: 8, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "struct_campfire_4", blueprintId: "campfire", position: { x: 8, y: 8, z: 0 }, health: 100, campfire: { ...campfireData } });

    // Extra campfires around the hell hole for light
    structures.push({ id: "struct_campfire_5", blueprintId: "campfire", position: { x: 0, y: -6, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "struct_campfire_6", blueprintId: "campfire", position: { x: 0, y: 6, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "struct_campfire_7", blueprintId: "campfire", position: { x: -6, y: 0, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "struct_campfire_8", blueprintId: "campfire", position: { x: 6, y: 0, z: 0 }, health: 100, campfire: { ...campfireData } });

    // ===== CHESTS AT PETAL ENDS =====
    // North petal - Resources
    structures.push({ id: "chest_north", blueprintId: "chest_wood", position: { x: 0, y: -19, z: 0 }, health: 100, storage: { slots: [
      { itemId: "wood", quantity: 99 }, { itemId: "wood", quantity: 99 }, { itemId: "wood", quantity: 99 },
      { itemId: "stone", quantity: 99 }, { itemId: "stone", quantity: 99 }, { itemId: "stone", quantity: 99 },
      { itemId: "iron_ore", quantity: 99 }, { itemId: "iron_ore", quantity: 99 }, { itemId: "iron_ore", quantity: 99 },
      { itemId: "fiber", quantity: 99 }, { itemId: "fiber", quantity: 99 }, { itemId: "fiber", quantity: 99 },
      { itemId: "bone", quantity: 50 }, { itemId: "bone", quantity: 50 },
      { itemId: "wolf_pelt", quantity: 20 }, { itemId: "wolf_pelt", quantity: 20 },
      { itemId: "golem_core", quantity: 10 }, { itemId: "golem_core", quantity: 10 }, null, null
    ] } });

    // South petal - Food
    structures.push({ id: "chest_south", blueprintId: "chest_wood", position: { x: 0, y: 19, z: 0 }, health: 100, storage: { slots: [
      { itemId: "apple", quantity: 20 }, { itemId: "apple", quantity: 20 }, { itemId: "apple", quantity: 20 },
      { itemId: "cooked_meat", quantity: 10 }, { itemId: "cooked_meat", quantity: 10 }, { itemId: "cooked_meat", quantity: 10 },
      { itemId: "raw_meat", quantity: 20 }, { itemId: "raw_meat", quantity: 20 },
      { itemId: "health_potion", quantity: 5 }, { itemId: "health_potion", quantity: 5 }, { itemId: "health_potion", quantity: 5 },
      { itemId: "bandage", quantity: 20 }, { itemId: "bandage", quantity: 20 },
      { itemId: "fur_cloak", quantity: 5 }, { itemId: "antidote", quantity: 10 },
      null, null, null, null, null
    ] } });

    // East petal - Melee Weapons
    structures.push({ id: "chest_east", blueprintId: "chest_wood", position: { x: 19, y: 0, z: 0 }, health: 100, storage: { slots: [
      { itemId: "sword_wood", quantity: 1 }, { itemId: "sword_stone", quantity: 1 }, { itemId: "sword_iron", quantity: 1 },
      { itemId: "spear_wood", quantity: 1 }, { itemId: "spear_stone", quantity: 1 }, { itemId: "wolf_fang_dagger", quantity: 1 },
      { itemId: "bone_club", quantity: 1 }, { itemId: "orc_cleaver", quantity: 1 }, { itemId: "golem_hammer", quantity: 1 },
      { itemId: "demon_blade", quantity: 1 }, { itemId: "spectral_scythe", quantity: 1 }, { itemId: "spider_fang", quantity: 1 },
      { itemId: "bone_knife", quantity: 1 }, { itemId: "bone_spear", quantity: 1 }, { itemId: "golem_sword", quantity: 1 },
      null, null, null, null, null
    ] } });

    // Extra chest - Primitive Guns (east petal side)
    structures.push({ id: "chest_primitive_guns", blueprintId: "chest_wood", position: { x: 18, y: 2, z: 0 }, health: 100, storage: { slots: [
      { itemId: "flintlock_pistol", quantity: 1 }, { itemId: "musket", quantity: 1 }, { itemId: "blunderbuss", quantity: 1 },
      { itemId: "musket_ball", quantity: 99 }, { itemId: "musket_ball", quantity: 99 }, { itemId: "musket_ball", quantity: 99 },
      { itemId: "buckshot", quantity: 99 }, { itemId: "buckshot", quantity: 99 }, { itemId: "buckshot", quantity: 99 },
      null, null, null, null, null, null, null, null, null, null, null
    ] } });

    // Extra chest - Modern Guns (east petal side)
    structures.push({ id: "chest_modern_guns", blueprintId: "chest_wood", position: { x: 18, y: -2, z: 0 }, health: 100, storage: { slots: [
      { itemId: "pistol_9mm", quantity: 1 }, { itemId: "smg", quantity: 1 }, { itemId: "assault_rifle", quantity: 1 },
      { itemId: "shotgun_pump", quantity: 1 }, { itemId: "sniper_rifle", quantity: 1 },
      { itemId: "pistol_ammo", quantity: 99 }, { itemId: "pistol_ammo", quantity: 99 }, { itemId: "rifle_ammo", quantity: 99 },
      { itemId: "rifle_ammo", quantity: 99 }, { itemId: "shotgun_shells", quantity: 99 }, { itemId: "shotgun_shells", quantity: 99 },
      null, null, null, null, null, null, null, null, null
    ] } });

    // Extra chest - Futuristic Guns (east petal end)
    structures.push({ id: "chest_futuristic_guns", blueprintId: "chest_wood", position: { x: 20, y: 2, z: 0 }, health: 100, storage: { slots: [
      { itemId: "laser_pistol", quantity: 1 }, { itemId: "laser_rifle", quantity: 1 }, { itemId: "pulse_smg", quantity: 1 },
      { itemId: "plasma_rifle", quantity: 1 }, { itemId: "plasma_cannon", quantity: 1 }, { itemId: "railgun", quantity: 1 },
      { itemId: "energy_cell", quantity: 99 }, { itemId: "energy_cell", quantity: 99 }, { itemId: "energy_cell", quantity: 99 },
      { itemId: "plasma_cell", quantity: 99 }, { itemId: "plasma_cell", quantity: 99 }, { itemId: "plasma_cell", quantity: 99 },
      { itemId: "rail_charge", quantity: 99 }, { itemId: "rail_charge", quantity: 99 }, { itemId: "rail_charge", quantity: 99 },
      null, null, null, null, null
    ] } });

    // West petal - Tools
    structures.push({ id: "chest_west", blueprintId: "chest_wood", position: { x: -19, y: 0, z: 0 }, health: 100, storage: { slots: [
      { itemId: "axe_wood", quantity: 1 }, { itemId: "axe_stone", quantity: 1 }, { itemId: "axe_iron", quantity: 1 },
      { itemId: "pickaxe_wood", quantity: 1 }, { itemId: "pickaxe_stone", quantity: 1 }, { itemId: "pickaxe_iron", quantity: 1 },
      { itemId: "sickle_wood", quantity: 1 }, { itemId: "sickle_stone", quantity: 1 },
      { itemId: "torch", quantity: 20 }, { itemId: "torch", quantity: 20 }, { itemId: "torch", quantity: 20 },
      { itemId: "torch", quantity: 20 }, { itemId: "torch", quantity: 20 },
      { itemId: "jackdrill", quantity: 1 }, null, null, null, null, null, null
    ] } });

    // Campfires at petal ends for lighting
    structures.push({ id: "campfire_north", blueprintId: "campfire", position: { x: -2, y: -19, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "campfire_south", blueprintId: "campfire", position: { x: -2, y: 19, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "campfire_east", blueprintId: "campfire", position: { x: 19, y: -2, z: 0 }, health: 100, campfire: { ...campfireData } });
    structures.push({ id: "campfire_west", blueprintId: "campfire", position: { x: -19, y: -2, z: 0 }, health: 100, campfire: { ...campfireData } });

    return structures;
  })()
};

export class SaveManager {
  private currentSlot: number = 0;
  private autoSaveEnabled: boolean = false;
  private autoSaveInterval: number = 180; // 3 minutes
  private lastAutoSave: number = 0;

  /** Callback when autosave completes */
  public onAutoSave: (() => void) | null = null;

  /** Save game to slot */
  save(data: SaveData, slot: number = this.currentSlot, name?: string): boolean {
    try {
      // If trying to save to the demo slot, find an empty slot instead
      if (slot === DEFAULT_SAVE_SLOT) {
        const emptySlot = this.findEmptySlot();
        if (emptySlot === -1) {
          // All slots full, use slot 0
          slot = 0;
          console.log('All save slots full, overwriting slot 0');
        } else {
          slot = emptySlot;
          console.log(`Demo save - using empty slot ${slot}`);
        }
        // Update current slot to the new slot
        this.currentSlot = slot;
      }

      // Get existing name if not provided
      const existingInfo = this.getSaveInfo(slot);
      const saveName = name ?? existingInfo.name ?? `Save ${slot + 1}`;

      const saveData: SaveData = {
        ...data,
        version: SAVE_VERSION,
        timestamp: Date.now(),
        name: saveName
      };

      const json = JSON.stringify(saveData);
      const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
      localStorage.setItem(key, json);

      // Track this as last played slot
      localStorage.setItem(LAST_PLAYED_KEY, slot.toString());

      console.log(`Game saved to slot ${slot} as "${saveName}"`);
      return true;
    } catch (e) {
      console.error('Failed to save:', e);
      return false;
    }
  }

  /** Load game from slot */
  load(slot: number): SaveData | null {
    try {
      // Handle default demo save
      if (slot === DEFAULT_SAVE_SLOT) {
        console.log('Loading default demo save');
        // Mark current slot as demo so saves go to a new slot
        this.currentSlot = DEFAULT_SAVE_SLOT;
        // Don't track demo as last played (can't save back to it)
        // Return a deep copy so modifications don't affect the original
        return JSON.parse(JSON.stringify(DEFAULT_SAVE_DATA));
      }

      const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
      const json = localStorage.getItem(key);

      if (!json) {
        console.log(`No save found in slot ${slot}`);
        return null;
      }

      const data = JSON.parse(json) as SaveData;

      // Version migration if needed
      if (data.version < SAVE_VERSION) {
        return this.migrate(data);
      }

      // Ensure name exists (for old saves)
      if (!data.name) {
        data.name = `Save ${slot + 1}`;
      }

      this.currentSlot = slot;

      // Track this as last played slot
      localStorage.setItem(LAST_PLAYED_KEY, slot.toString());

      console.log(`Game loaded from slot ${slot}: "${data.name}"`);
      return data;
    } catch (e) {
      console.error('Failed to load:', e);
      return null;
    }
  }

  /** Delete save in slot */
  deleteSave(slot: number): boolean {
    // Cannot delete the default demo save
    if (slot === DEFAULT_SAVE_SLOT) {
      console.log('Cannot delete default demo save');
      return false;
    }

    try {
      const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
      localStorage.removeItem(key);
      console.log(`Save deleted from slot ${slot}`);
      return true;
    } catch (e) {
      console.error('Failed to delete save:', e);
      return false;
    }
  }

  /** Get info about a save slot */
  getSaveInfo(slot: number): SaveSlotInfo {
    try {
      const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
      const json = localStorage.getItem(key);

      if (!json) {
        return { slot, exists: false, name: '', timestamp: 0, playTime: 0, day: 0 };
      }

      const data = JSON.parse(json) as SaveData;
      return {
        slot,
        exists: true,
        name: data.name || `Save ${slot + 1}`,
        timestamp: data.timestamp,
        playTime: data.time.totalPlayTime,
        day: data.time.day
      };
    } catch {
      return { slot, exists: false, name: '', timestamp: 0, playTime: 0, day: 0 };
    }
  }

  /** Get info about all save slots */
  getAllSaveInfo(): SaveSlotInfo[] {
    const info: SaveSlotInfo[] = [];
    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      info.push(this.getSaveInfo(i));
    }
    // Add default demo save at the end
    info.push(this.getDefaultSaveInfo());
    return info;
  }

  /** Get info about the default demo save */
  getDefaultSaveInfo(): SaveSlotInfo {
    return {
      slot: DEFAULT_SAVE_SLOT,
      exists: true,
      name: DEFAULT_SAVE_DATA.name,
      timestamp: DEFAULT_SAVE_DATA.timestamp,
      playTime: DEFAULT_SAVE_DATA.time.totalPlayTime,
      day: DEFAULT_SAVE_DATA.time.day
    };
  }

  /** Check if a slot is the default demo save */
  isDefaultSave(slot: number): boolean {
    return slot === DEFAULT_SAVE_SLOT;
  }

  /** Check if slot has save */
  hasSave(slot: number): boolean {
    const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
    return localStorage.getItem(key) !== null;
  }

  /** Export save as JSON string */
  exportSave(slot: number): string | null {
    const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
    return localStorage.getItem(key);
  }

  /** Import save from JSON string */
  importSave(json: string, slot: number): boolean {
    try {
      // Validate JSON
      const data = JSON.parse(json) as SaveData;
      if (!data.version || !data.player || !data.inventory) {
        throw new Error('Invalid save data');
      }

      const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
      localStorage.setItem(key, json);
      return true;
    } catch (e) {
      console.error('Failed to import save:', e);
      return false;
    }
  }

  /** Migrate old save format to current */
  private migrate(data: SaveData): SaveData {
    // Add migrations as needed when version changes
    // For now, just update version number
    data.version = SAVE_VERSION;
    return data;
  }

  /** Update for auto-save */
  update(deltaTime: number, getSaveData: () => SaveData): void {
    if (!this.autoSaveEnabled) return;

    this.lastAutoSave += deltaTime;

    if (this.lastAutoSave >= this.autoSaveInterval) {
      this.lastAutoSave = 0;
      const success = this.save(getSaveData());
      if (success) {
        console.log('Auto-saved');
        this.onAutoSave?.();
      }
    }
  }

  /** Enable/disable auto-save */
  setAutoSave(enabled: boolean, interval?: number): void {
    this.autoSaveEnabled = enabled;
    if (interval) {
      this.autoSaveInterval = interval;
    }
  }

  /** Get current slot */
  getCurrentSlot(): number {
    return this.currentSlot;
  }

  /** Set current slot */
  setCurrentSlot(slot: number): void {
    this.currentSlot = Math.max(0, Math.min(MAX_SAVE_SLOTS - 1, slot));
  }

  /** Get the last played save slot */
  getLastPlayedSlot(): number | null {
    try {
      const slot = localStorage.getItem(LAST_PLAYED_KEY);
      if (slot === null) return null;
      const slotNum = parseInt(slot, 10);
      if (isNaN(slotNum) || !this.hasSave(slotNum)) return null;
      return slotNum;
    } catch {
      return null;
    }
  }

  /** Rename a save */
  renameSave(slot: number, newName: string): boolean {
    try {
      const key = `${LOCAL_STORAGE_PREFIX}save_${slot}`;
      const json = localStorage.getItem(key);
      if (!json) return false;

      const data = JSON.parse(json) as SaveData;
      data.name = newName;
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`Save ${slot} renamed to "${newName}"`);
      return true;
    } catch (e) {
      console.error('Failed to rename save:', e);
      return false;
    }
  }

  /** Swap two saves between slots */
  swapSaves(slotA: number, slotB: number): boolean {
    // Cannot swap with demo slot
    if (slotA === DEFAULT_SAVE_SLOT || slotB === DEFAULT_SAVE_SLOT) {
      console.log('Cannot swap with demo save');
      return false;
    }

    try {
      const keyA = `${LOCAL_STORAGE_PREFIX}save_${slotA}`;
      const keyB = `${LOCAL_STORAGE_PREFIX}save_${slotB}`;

      const jsonA = localStorage.getItem(keyA);
      const jsonB = localStorage.getItem(keyB);

      // Swap the data
      if (jsonA) {
        localStorage.setItem(keyB, jsonA);
      } else {
        localStorage.removeItem(keyB);
      }

      if (jsonB) {
        localStorage.setItem(keyA, jsonB);
      } else {
        localStorage.removeItem(keyA);
      }

      console.log(`Swapped saves between slot ${slotA} and ${slotB}`);
      return true;
    } catch (e) {
      console.error('Failed to swap saves:', e);
      return false;
    }
  }

  /** Find the first empty save slot */
  findEmptySlot(): number {
    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      if (!this.hasSave(i)) {
        return i;
      }
    }
    return -1; // All slots full
  }

  /** Get max save slots */
  getMaxSlots(): number {
    return MAX_SAVE_SLOTS;
  }

  /** Check if there are any saves */
  hasAnySave(): boolean {
    for (let i = 0; i < MAX_SAVE_SLOTS; i++) {
      if (this.hasSave(i)) return true;
    }
    return false;
  }
}
